import {
	DeviceState,
	DeviceStatus,
} from "Infrastructure/Models/Messages/Dto/DeviceStatus";
import { ProductStatus } from "Infrastructure/Models/Messages/Dto/ProductStatus";
import { FunctionComponent, useEffect, useState } from "react";
import { CircularProgress, Stack } from "@mui/material";
import { HubConnection, HubConnectionState } from "@microsoft/signalr";
import { CloseDialogHandler } from "Infrastructure/Providers/DialogProvider/DialogProvider";
import { ProductEnableDisableMessage } from "Infrastructure/Models/Messages/Messages";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { useSnackbar } from "Infrastructure/Providers/SnackbarProvider/SnackbarProvider";
import { logError } from "Infrastructure/Utils/LoggingUtils";
import InstanceResponseContent from "../InstanceResponseContent";
import ProductActions from "./ProductActions";
import {
	mergeDeviceStatuses,
	ProductStatusDeviceModel,
} from "Infrastructure/Utils/Nsure/ProductUtils";

interface Props {
	deviceStatuses: DeviceStatus[] | null;
	deviceStatus?: DeviceStatus | undefined;
	hubConnection: HubConnection | null;
	closeDialog: CloseDialogHandler;
}

const ProductsDialogContent: FunctionComponent<Props> = (props) => {
	const { deviceStatuses, deviceStatus, hubConnection, closeDialog } = props;
	const showSnackbar = useSnackbar();
	const { t } = useResource();

	const [isLoading, setIsLoading] = useState(true);
	const [productDeviceModel, setProductDeviceModel] =
		useState<ProductStatusDeviceModel | null>(null);

	const [messageResponse, setMessageResponse] = useState<{
		actionName: string;
		requestId?: string | null;
		deviceNames: string[];
	} | null>(null);

	// biome-ignore lint/correctness/useExhaustiveDependencies: hubConnection is stable for the dialog lifetime
	useEffect(() => {
		if (!deviceStatuses) {
			return;
		}
		setIsLoading(true);
		const applyModel = (
			productStatusByDevice?: Record<string, ProductStatus>
		) => {
			setProductDeviceModel(
				mergeDeviceStatuses(deviceStatuses, deviceStatus, productStatusByDevice)
			);
			setIsLoading(false);
		};
		const deviceNames = deviceStatuses
			.filter(
				(x) => !x.isSelfInsurance && (!!deviceStatus ? x === deviceStatus : true)
			)
			.map((x) => x.deviceName);
		if (
			deviceNames.length === 0 ||
			!hubConnection ||
			hubConnection.state !== HubConnectionState.Connected
		) {
			applyModel();
			return;
		}
		hubConnection
			.invoke<Record<string, ProductStatus>>(
				"getDeviceProductStatusRequest",
				deviceNames
			)
			.then((result) => applyModel(result ?? {}))
			.catch((error: Error) => {
				logError(error);
				applyModel();
			});
	}, [deviceStatuses, deviceStatus]);

	const handleProductEnableDisable = (
		productEnableDisableMessage: ProductEnableDisableMessage
	) => {
		if (
			!hubConnection ||
			hubConnection.state !== HubConnectionState.Connected
		) {
			showSnackbar(t(Resources.Errors.Hub.ConnectionError), "error");
			return;
		}
		setMessageResponse({
			actionName: "Zapnutí/vypnutí",
			requestId: productEnableDisableMessage.requestId,
			deviceNames: productEnableDisableMessage.filteredDeviceNames,
		});
		hubConnection
			.invoke("productEnableDisable", productEnableDisableMessage)
			.catch((error: Error) => {
				showSnackbar(t(Resources.Errors.Hub.RequestError), "error");
				logError(error);
			});
	};

	if (messageResponse) {
		return (
			<InstanceResponseContent
				actionName={messageResponse.actionName}
				requestId={messageResponse.requestId}
				deviceNames={messageResponse.deviceNames}
				hubConnection={hubConnection}
				closeDialog={closeDialog}
			/>
		);
	}

	return (
		<Stack direction="column">
			{isLoading ? (
				<>
					<CircularProgress />
				</>
			) : (
				<>
					{deviceStatuses && deviceStatuses?.length > 0 && (
						<ProductActions
							deviceStatuses={deviceStatuses}
							deviceStatus={deviceStatus}
							productDeviceModel={productDeviceModel}
							hubConnection={hubConnection}
							closeDialog={closeDialog}
							onEnableDisable={handleProductEnableDisable}
						/>
					)}
				</>
			)}
		</Stack>
	);
};

export default ProductsDialogContent;
